export const Permissions = {

    // Dashboard — each widget requires its own permission
    DASHBOARD_VIEW:              'dashboard.view',
    DASHBOARD_ADMIN_VIEW:        'dashboard.admin.view',
    DASHBOARD_WIDGET_KPI:        'dashboard.widget.kpi',
    DASHBOARD_WIDGET_PROCESSES:  'dashboard.widget.processes',
    DASHBOARD_WIDGET_GOALS:      'dashboard.widget.goals',
    DASHBOARD_WIDGET_RISKS:      'dashboard.widget.risks',
    DASHBOARD_WIDGET_COMPLAINTS: 'dashboard.widget.complaints',
    DASHBOARD_WIDGET_AUDIT:      'dashboard.widget.audit',
    DASHBOARD_WIDGET_TASKS:      'dashboard.widget.tasks',
    DASHBOARD_WIDGET_MEETINGS:   'dashboard.widget.meetings',
    DASHBOARD_WIDGET_TRAINING:   'dashboard.widget.training',

    // Processes — BRD-02 (FE_REINTEGRATION_GUIDE.md §4, HEAD 83fd565): axes
    // edit/submit are now gated by `process.create`; claim/comment/edit-forward/
    // edit-return/review-approve are now gated by `process.review` (a real
    // permission — the earlier product-decision gap is resolved);
    // delegations create/delete are now gated by `process.approve`.
    // `process.edit-axes`/`process.comment-axes`/`process.approve-review`/
    // `process.delegate-approval` are no longer used by any endpoint.
    PROCESS_VIEW:           'process.view',
    PROCESS_CREATE:         'process.create',
    PROCESS_EDIT:           'process.create',
    PROCESS_DELETE:         'process.delete',
    /** @deprecated No backend permission constant exists — submit is gated by `process.create` instead. Dead string, no call site should treat this as an enforced gate. Kept defined only for traceability. */
    PROCESS_SEND_REVIEW:    'process.send_review',
    PROCESS_REVIEW:         'process.review',
    /** @deprecated No longer used by any endpoint — comment-axes is now gated by `process.review`. Kept defined only for traceability. */
    PROCESS_COMMENT:        'process.review',
    /** @deprecated No longer used by any endpoint — review-approve is now gated by `process.review`. Kept defined only for traceability. */
    PROCESS_APPROVE_REVIEW: 'process.review',
    PROCESS_APPROVE:        'process.approve',
    PROCESS_DELEGATE:       'process.approve',
    PROCESS_FINAL_APPROVE:  'process.final-approve',
    /** @deprecated No backend permission counterpart exists at all — export was never modeled as a permission server-side, and no export endpoint exists either (see BACKEND-MAP.md's Permissions section). The process-list export button is already hidden (`canExport` hardcoded false) rather than gated by this string. */
    PROCESS_EXPORT:         'process.export',

    // Quality Goals — BRD-03 (backend module name: QualityObjectives)
    // TODO(confirm-with-basem): reconciled 2026-09-04 against BACKEND-MAP.md's
    // "Quality Objectives APIs" section — the real backend exposes exactly 3
    // permission strings (quality-objectives.create/.view/.system-manager),
    // used across all 3 controllers (Register/Detail/FollowUpReport) for every
    // action, including claim/approve/return (all gated by `.view`, not a
    // separate "approve" permission — this is a real backend quirk, not a typo).
    // The previous GOALS_EDIT/GOALS_DELETE/GOALS_APPROVE/GOALS_FINAL_APPROVE/
    // GOALS_FOLLOWUP/QUALITY_OBJECTIVES_APPROVE strings had no backend
    // permission counterpart and are removed; QUALITY_OBJECTIVES_REVIEW is kept
    // as an alias pointing at `.view` since the review/act routes (register
    // review, detail review, report review) are gated the same as any other
    // `.view`-permitted action per the endpoint table.
    GOALS_VIEW:          'quality-objectives.view',
    QUALITY_OBJECTIVES_CREATE:        'quality-objectives.create',
    GOALS_SYSTEM_MANAGER: 'quality-objectives.system-manager',
    QUALITY_OBJECTIVES_REVIEW: 'quality-objectives.view',

    // KPIs — BRD-06
    // TODO(confirm-with-basem): gap-reports/GAP-BRD06-KPIs.md §3 confirms
    // `KpiController` has zero [Authorize]/[HasPermission] attributes and
    // `PermissionNames.cs` has zero KPI/indicator/measurement entries at all
    // — these five strings have NO backend counterpart to remap to; they are
    // left as-is (frontend-only gate) until permission names are registered
    // server-side. Do not treat these as verified backend permissions.
    KPI_VIEW:         'kpi.view',
    KPI_CREATE:       'kpi.create',
    KPI_EDIT:         'kpi.edit',
    KPI_MEASURE:      'kpi.measure',
    KPI_REVIEW:       'kpi.review',
    KPI_APPROVE:      'kpi.approve',
    KPI_FINAL_APPROVE: 'kpi.final-approve',

    // Risks
    /** @deprecated No call site uses this string — no backend permission of this name exists (backend only defines `risk-opportunity.*`, see gap-reports/GAP-BRD05-Risk-Management.md §3). Kept defined only for traceability. Never use in a guard. */
    RISK_VIEW:    'risk.view',
    /** @deprecated No call site uses this string — no backend permission of this name exists (backend only defines `risk-opportunity.*`, see gap-reports/GAP-BRD05-Risk-Management.md §3). Kept defined only for traceability. Never use in a guard. */
    RISK_CREATE:  'risk.create',
    /** @deprecated No call site uses this string — no backend permission of this name exists (backend only defines `risk-opportunity.*`, see gap-reports/GAP-BRD05-Risk-Management.md §3). Kept defined only for traceability. Never use in a guard. */
    RISK_EDIT:    'risk.edit',
    /** @deprecated No call site uses this string — no backend permission of this name exists (backend only defines `risk-opportunity.*`, see gap-reports/GAP-BRD05-Risk-Management.md §3). Kept defined only for traceability. Never use in a guard. */
    RISK_APPROVE: 'risk.approve',

    // Internal Audit
    // TODO: fine-grained permissions pending backend — the backend only defines 5 real
    // internal-audit permissions (internal-audit.view/create/review/approve/final-approve,
    // see gap-reports/GAP-BRD04-Internal-Audit.md §3/§8-point-4); none of the ~30 AUDIT_*
    // strings below have a matching backend permission. Left as-is (no rename, no deletion,
    // guards unchanged) pending a product decision — do not map these to the 5 real names
    // without confirmation, since it's not a 1:1 relationship.
    AUDIT_VIEW:    'audit.view',
    AUDIT_CREATE:  'audit.create',
    AUDIT_EDIT:    'audit.edit',
    AUDIT_APPROVE: 'audit.approve',

    // Internal Audit — Settings
    AUDIT_SETTINGS_VIEW:                'audit.settings.view',
    AUDIT_SETTINGS_MODULE_CODES_MANAGE: 'audit.settings.module_codes.manage',
    AUDIT_SETTINGS_SUB_CODES_MANAGE:    'audit.settings.sub_codes.manage',
    AUDIT_SETTINGS_ORG_CODES_MANAGE:    'audit.settings.org_codes.manage',
    AUDIT_SETTINGS_ESCALATION_MANAGE:   'audit.settings.escalation.manage',
    /** @deprecated No call site uses this string — the users screen gates on plain `USERS_VIEW` instead, and no backend permission of this name exists (see gap-reports/GAP-SystemSettings.md §3). Kept defined only for traceability. */
    AUDIT_SETTINGS_USERS_VIEW:          'audit.settings.users.view',
    /** @deprecated No call site uses this string — the users screen gates on plain `USERS_MANAGE` instead, and no backend permission of this name exists (see gap-reports/GAP-SystemSettings.md §3). Kept defined only for traceability. */
    AUDIT_SETTINGS_USERS_MANAGE:        'audit.settings.users.manage',

    // Internal Audit — Setup
    AUDIT_SETUP_REVIEWERS_VIEW:    'audit.setup.reviewers.view',
    AUDIT_SETUP_REVIEWERS_CREATE:  'audit.setup.reviewers.create',
    AUDIT_SETUP_REVIEWERS_EDIT:    'audit.setup.reviewers.edit',
    AUDIT_SETUP_REVIEWERS_APPROVE: 'audit.setup.reviewers.approve',
    AUDIT_SETUP_ITEMS_MANAGE:      'audit.setup.items.manage',

    // Internal Audit — Planning
    AUDIT_PLANNING_VIEW:            'audit.planning.view',
    AUDIT_PLANNING_ANNUAL_CREATE:   'audit.planning.annual.create',
    AUDIT_PLANNING_ANNUAL_APPROVE:  'audit.planning.annual.approve',
    AUDIT_PLANNING_EXECUTION_VIEW:    'audit.planning.execution.view',
    AUDIT_PLANNING_EXECUTION_CREATE:  'audit.planning.execution.create',
    AUDIT_PLANNING_EXECUTION_APPROVE: 'audit.planning.execution.approve',

    // Internal Audit — Unplanned
    AUDIT_UNPLANNED_VIEW:    'audit.unplanned.view',
    AUDIT_UNPLANNED_CREATE:  'audit.unplanned.create',
    AUDIT_UNPLANNED_APPROVE: 'audit.unplanned.approve',

    // Internal Audit — Execution
    AUDIT_EXECUTION_VISITS_VIEW:        'audit.execution.visits.view',
    AUDIT_EXECUTION_ASSIGNMENT_MANAGE:  'audit.execution.assignment.manage',
    AUDIT_EXECUTION_ASSIGNMENT_APPROVE: 'audit.execution.assignment.approve',
    AUDIT_EXECUTION_VISIT_OPEN:         'audit.execution.visit.open',
    AUDIT_EXECUTION_VISIT_CLOSE:        'audit.execution.visit.close',
    AUDIT_EXECUTION_VISIT_APPROVE:      'audit.execution.visit.approve',
    AUDIT_EXECUTION_CHECKLIST_FILL:     'audit.execution.checklist.fill',
    AUDIT_EXECUTION_NCR_CREATE:         'audit.execution.ncr.create',

    // Internal Audit — NCR
    AUDIT_NCR_VIEW:            'audit.ncr.view',
    AUDIT_NCR_PROCESS:         'audit.ncr.process',
    AUDIT_NCR_APPROVE:         'audit.ncr.approve',
    AUDIT_NCR_FOLLOWUP:        'audit.ncr.followup',
    AUDIT_NCR_FOLLOWUP_REVIEW: 'audit.ncr.followup.review',
    AUDIT_NCR_CLOSE:           'audit.ncr.close',

    // Internal Audit — Reports
    AUDIT_REPORTS_REGISTER_VIEW:       'audit.reports.register.view',
    AUDIT_REPORTS_VISIT_REPORT_VIEW:   'audit.reports.visit_report.view',
    AUDIT_REPORTS_PERIOD_REPORT_CREATE: 'audit.reports.period_report.create',
    AUDIT_REPORTS_ANNUAL_REPORT_VIEW:  'audit.reports.annual_report.view',

    // Risk Management
    /** @deprecated No call site uses this string — no backend permission of this name exists (backend only defines `risk-opportunity.*`, see gap-reports/GAP-BRD05-Risk-Management.md §3). Kept defined only for traceability. Never use in a guard. */
    RISK_ISSUES_VIEW:    'risk.issues.view',
    /** @deprecated No call site uses this string — no backend permission of this name exists (backend only defines `risk-opportunity.*`, see gap-reports/GAP-BRD05-Risk-Management.md §3). Kept defined only for traceability. Never use in a guard. */
    RISK_ISSUES_CREATE:  'risk.issues.create',
    /** @deprecated No call site uses this string — no backend permission of this name exists (backend only defines `risk-opportunity.*`, see gap-reports/GAP-BRD05-Risk-Management.md §3). Kept defined only for traceability. Never use in a guard. */
    RISK_ISSUES_EDIT:    'risk.issues.edit',
    /** @deprecated No call site uses this string — no backend permission of this name exists (backend only defines `risk-opportunity.*`, see gap-reports/GAP-BRD05-Risk-Management.md §3). Kept defined only for traceability. Never use in a guard. */
    RISK_ISSUES_APPROVE: 'risk.issues.approve',

    /** @deprecated No call site uses this string — no backend permission of this name exists (backend only defines `risk-opportunity.*`, see gap-reports/GAP-BRD05-Risk-Management.md §3). Kept defined only for traceability. Never use in a guard. */
    RISK_STAKEHOLDERS_VIEW:    'risk.stakeholders.view',
    /** @deprecated No call site uses this string — no backend permission of this name exists (backend only defines `risk-opportunity.*`, see gap-reports/GAP-BRD05-Risk-Management.md §3). Kept defined only for traceability. Never use in a guard. */
    RISK_STAKEHOLDERS_CREATE:  'risk.stakeholders.create',
    /** @deprecated No call site uses this string — no backend permission of this name exists (backend only defines `risk-opportunity.*`, see gap-reports/GAP-BRD05-Risk-Management.md §3). Kept defined only for traceability. Never use in a guard. */
    RISK_STAKEHOLDERS_EDIT:    'risk.stakeholders.edit',
    /** @deprecated No call site uses this string — no backend permission of this name exists (backend only defines `risk-opportunity.*`, see gap-reports/GAP-BRD05-Risk-Management.md §3). Kept defined only for traceability. Never use in a guard. */
    RISK_STAKEHOLDERS_APPROVE: 'risk.stakeholders.approve',

    /** @deprecated No call site uses this string — no backend permission of this name exists (backend only defines `risk-opportunity.*`, see gap-reports/GAP-BRD05-Risk-Management.md §3). Kept defined only for traceability. Never use in a guard. */
    RISK_REGISTER_VIEW:    'risk.register.view',
    /** @deprecated No call site uses this string — no backend permission of this name exists (backend only defines `risk-opportunity.*`, see gap-reports/GAP-BRD05-Risk-Management.md §3). Kept defined only for traceability. Never use in a guard. */
    RISK_REGISTER_CREATE:  'risk.register.create',
    /** @deprecated No call site uses this string — no backend permission of this name exists (backend only defines `risk-opportunity.*`, see gap-reports/GAP-BRD05-Risk-Management.md §3). Kept defined only for traceability. Never use in a guard. */
    RISK_REGISTER_APPROVE: 'risk.register.approve',

    /** @deprecated No call site uses this string — no backend permission of this name exists (backend only defines `risk-opportunity.*`, see gap-reports/GAP-BRD05-Risk-Management.md §3). Kept defined only for traceability. Never use in a guard. */
    RISK_REPORT_VIEW:    'risk.report.view',
    /** @deprecated No call site uses this string — no backend permission of this name exists (backend only defines `risk-opportunity.*`, see gap-reports/GAP-BRD05-Risk-Management.md §3). Kept defined only for traceability. Never use in a guard. */
    RISK_REPORT_CREATE:  'risk.report.create',
    /** @deprecated No call site uses this string — no backend permission of this name exists (backend only defines `risk-opportunity.*`, see gap-reports/GAP-BRD05-Risk-Management.md §3). Kept defined only for traceability. Never use in a guard. */
    RISK_REPORT_APPROVE: 'risk.report.approve',

    /** @deprecated No call site uses this string — no backend permission of this name exists (backend only defines `risk-opportunity.*`, see gap-reports/GAP-BRD05-Risk-Management.md §3). Kept defined only for traceability. Never use in a guard. */
    RISK_PLANS_VIEW:    'risk.plans.view',
    /** @deprecated No call site uses this string — no backend permission of this name exists (backend only defines `risk-opportunity.*`, see gap-reports/GAP-BRD05-Risk-Management.md §3). Kept defined only for traceability. Never use in a guard. */
    RISK_PLANS_CREATE:  'risk.plans.create',
    /** @deprecated No call site uses this string — no backend permission of this name exists (backend only defines `risk-opportunity.*`, see gap-reports/GAP-BRD05-Risk-Management.md §3). Kept defined only for traceability. Never use in a guard. */
    RISK_PLANS_APPROVE: 'risk.plans.approve',

    /** @deprecated No call site uses this string — no backend permission of this name exists (backend only defines `risk-opportunity.*`, see gap-reports/GAP-BRD05-Risk-Management.md §3). Kept defined only for traceability. Never use in a guard. */
    RISK_FOLLOWUP_VIEW:    'risk.followup.view',
    /** @deprecated No call site uses this string — no backend permission of this name exists (backend only defines `risk-opportunity.*`, see gap-reports/GAP-BRD05-Risk-Management.md §3). Kept defined only for traceability. Never use in a guard. */
    RISK_FOLLOWUP_SUBMIT:  'risk.followup.submit',
    /** @deprecated No call site uses this string — no backend permission of this name exists (backend only defines `risk-opportunity.*`, see gap-reports/GAP-BRD05-Risk-Management.md §3). Kept defined only for traceability. Never use in a guard. */
    RISK_FOLLOWUP_APPROVE: 'risk.followup.approve',

    // Risk Management
    RISK_OPPORTUNITY_VIEW:          'risk-opportunity.view',
    RISK_OPPORTUNITY_CREATE:        'risk-opportunity.create',
    RISK_OPPORTUNITY_REVIEW:        'risk-opportunity.review',
    RISK_OPPORTUNITY_APPROVE:       'risk-opportunity.approve',
    RISK_OPPORTUNITY_FINAL_APPROVE: 'risk-opportunity.final-approve',

    // Complaints
    COMPLAINT_VIEW:    'complaint.view',
    COMPLAINT_CREATE:  'complaint.create',
    COMPLAINT_EDIT:    'complaint.edit',
    COMPLAINT_APPROVE: 'complaint.approve',

    // Surveys
    SURVEY_VIEW:   'survey.view',
    SURVEY_CREATE: 'survey.create',
    SURVEY_EDIT:   'survey.edit',

    // Training
    TRAINING_VIEW:   'training.view',
    TRAINING_CREATE: 'training.create',
    TRAINING_EDIT:   'training.edit',

    // Meetings
    MEETING_VIEW:   'meeting.view',
    MEETING_CREATE: 'meeting.create',
    MEETING_EDIT:   'meeting.edit',

    // Tasks
    TASK_VIEW:   'task.view',
    TASK_CREATE: 'task.create',
    TASK_EDIT:   'task.edit',

    // Knowledge
    KNOWLEDGE_VIEW:   'knowledge.view',
    KNOWLEDGE_CREATE: 'knowledge.create',
    KNOWLEDGE_EDIT:   'knowledge.edit',

    // Org Structure
    ORG_VIEW:   'org.view',
    ORG_CREATE: 'org.create',
    ORG_EDIT:   'org.edit',

    // Settings
    SETTINGS_VIEW: 'settings.view',
    SETTINGS_EDIT: 'settings.edit',

    // System Admin only — USERS_VIEW/MANAGE match backend PermissionNames.All exactly.
    // USERS_CREATE/EDIT/DELETE do NOT exist on the backend (it only grants users.manage
    // for every mutation) — kept only because PERMISSION_MODULES already renders them as
    // pickable checkboxes on the request/approve/manage screens; see PROJECT-MAP.md.
    USERS_VIEW:   'users.view',
    USERS_MANAGE: 'users.manage',
    USERS_CREATE: 'users.create',
    USERS_EDIT:   'users.edit',
    USERS_DELETE: 'users.delete',

    // Roles & permission requests — match backend PermissionNames.All exactly.
    ROLES_MANAGE:               'roles.manage',
    PERMISSION_REQUESTS_MANAGE: 'permission-requests.manage',

    // Import system (NEW — FE_REINTEGRATION_GUIDE.md §12/§15)
    IMPORT_VIEW:                    'import.view',
    AUDIT_REVIEW_ITEMS_IMPORT:      'audit.review-items.import',
    AUDIT_PLANNING_ANNUAL_IMPORT:   'audit.planning.annual.import',
    AUDIT_PLANNING_EXECUTION_IMPORT: 'audit.planning.execution.import',
    AUDIT_AUDITORS_IMPORT:          'audit.auditors.import',
    PROCESS_IMPORT:                 'process.import',
    KPI_IMPORT:                     'kpi.import',
    RISK_REGISTER_IMPORT:           'risk.register.import',
    ORG_UNITS_IMPORT:               'org.units.import',

} as const;

export type Permission = typeof Permissions[keyof typeof Permissions];
